import { z } from 'zod';
import { CUSTOM_FIELD_TYPES, STATUS_TYPES, VIEW_TYPES } from '../constants';
import { FILTER_OPS, type FilterGroup } from '../tasks';
import { colorSchema, nameSchema, positionSchema } from './workspace';

const id = z.string().min(1).max(64);
const idList = z.array(id).max(200);
const ms = z.number().int().nullable();
/** Tiptap JSON. Validated structurally only; size capped by the request body limit. */
const richText = z.unknown();

export const taskNameSchema = z.string().trim().min(1, 'Task name is required').max(500);

export const createTaskSchema = z.object({
  listId: id,
  parentId: id.nullable().optional(),
  name: taskNameSchema,
  description: richText.optional(),
  statusId: id.optional(),
  priority: z.number().int().min(0).max(4).optional(),
  startDate: ms.optional(),
  startHasTime: z.boolean().optional(),
  dueDate: ms.optional(),
  dueHasTime: z.boolean().optional(),
  timeEstimateMs: z.number().int().min(0).nullable().optional(),
  points: z.number().min(0).nullable().optional(),
  assigneeIds: idList.optional(),
  teamIds: idList.optional(),
  tagIds: idList.optional(),
  fields: z.record(z.string(), z.unknown()).optional(),
  position: positionSchema.optional(),
  /** Client generated id, so optimistic rows keep their identity. */
  id: z
    .string()
    .regex(/^tsk_[A-Za-z0-9]{16}$/)
    .optional(),
});
export type CreateTaskInput = z.infer<typeof createTaskSchema>;

export const updateTaskSchema = z.object({
  name: taskNameSchema.optional(),
  description: richText.optional(),
  statusId: id.optional(),
  priority: z.number().int().min(0).max(4).optional(),
  startDate: ms.optional(),
  startHasTime: z.boolean().optional(),
  dueDate: ms.optional(),
  dueHasTime: z.boolean().optional(),
  timeEstimateMs: z.number().int().min(0).nullable().optional(),
  points: z.number().min(0).nullable().optional(),
  /** Full replacement of the assignee set. */
  assigneeIds: idList.optional(),
  addAssigneeIds: idList.optional(),
  removeAssigneeIds: idList.optional(),
  /** Full replacement of the teams the task is assigned to. */
  teamIds: idList.optional(),
  tagIds: idList.optional(),
  addTagIds: idList.optional(),
  removeTagIds: idList.optional(),
  watcherIds: idList.optional(),
  /** Custom field values by field id; null clears a value. */
  fields: z.record(z.string(), z.unknown()).optional(),
  position: positionSchema.optional(),
  /** Move to another list (subtasks follow). */
  listId: id.optional(),
  /** Reparent; null makes it a top level task. */
  parentId: id.nullable().optional(),
  /** Extra lists the task also appears in. */
  extraListIds: idList.optional(),
  archived: z.boolean().optional(),
});
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

export const bulkTasksSchema = z.object({
  ids: z.array(id).min(1).max(200),
  patch: updateTaskSchema.optional(),
  delete: z.boolean().optional(),
});
export type BulkTasksInput = z.infer<typeof bulkTasksSchema>;

export const taskQuerySchema = z.object({
  scope: z.enum(['list', 'folder', 'space', 'everything']),
  id: z.string().optional(),
  includeClosed: z.enum(['true', 'false']).optional(),
  /** `me` or a user id: only tasks assigned to that user. */
  assignee: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(2000).optional(),
});

// ---------- Statuses ----------

export const statusInputSchema = z.object({
  /** Present for existing statuses; missing for new ones. */
  id: id.optional(),
  name: z.string().trim().min(1).max(60),
  color: colorSchema,
  type: z.enum(STATUS_TYPES),
});

export const saveStatusSetSchema = z.object({
  scopeType: z.enum(['space', 'folder', 'list']),
  scopeId: id,
  /** In display order. Must include at least one open and one done or closed status. */
  statuses: z.array(statusInputSchema).min(2).max(30),
  /** Where tasks in removed statuses go: removed status id to a kept status id (or new status name). */
  remap: z.record(z.string(), z.string()).optional(),
});
export type SaveStatusSetInput = z.infer<typeof saveStatusSetSchema>;

export const deleteStatusSetSchema = z.object({
  scopeType: z.enum(['folder', 'list']),
  scopeId: id,
});

// ---------- Custom fields ----------

const fieldOption = z.object({ id: z.string().min(1).max(40), label: z.string().trim().min(1).max(80), color: colorSchema });
export const fieldConfigSchema = z
  .object({
    options: z.array(fieldOption).max(100).optional(),
    currency: z.string().length(3).optional(),
    precision: z.number().int().min(0).max(6).optional(),
    max: z.number().int().min(1).max(10).optional(),
    formula: z.string().max(1000).optional(),
  })
  .default({});

export const createFieldSchema = z.object({
  scopeType: z.enum(['workspace', 'space', 'folder', 'list']),
  scopeId: id,
  name: nameSchema,
  type: z.enum(CUSTOM_FIELD_TYPES),
  config: fieldConfigSchema.optional(),
});
export const updateFieldSchema = z.object({
  name: nameSchema.optional(),
  config: fieldConfigSchema.optional(),
  position: positionSchema.optional(),
});
export type CreateFieldInput = z.infer<typeof createFieldSchema>;
export type UpdateFieldInput = z.infer<typeof updateFieldSchema>;

// ---------- Tags ----------

export const createTagSchema = z.object({ spaceId: id, name: z.string().trim().min(1).max(40), color: colorSchema.optional() });
export const updateTagSchema = z.object({ name: z.string().trim().min(1).max(40).optional(), color: colorSchema.optional() });

// ---------- Checklists ----------

export const createChecklistSchema = z.object({ name: z.string().trim().min(1).max(200).default('Checklist') });
export const updateChecklistSchema = z.object({ name: z.string().trim().min(1).max(200).optional(), position: positionSchema.optional() });
export const createChecklistItemSchema = z.object({ name: z.string().trim().min(1).max(500), assigneeId: id.nullable().optional() });
export const updateChecklistItemSchema = z.object({
  name: z.string().trim().min(1).max(500).optional(),
  done: z.boolean().optional(),
  assigneeId: id.nullable().optional(),
  position: positionSchema.optional(),
});

// ---------- Dependencies and links ----------

export const addDependencySchema = z.object({
  /** `waiting_on`: this task waits on the other. `blocking`: this task blocks the other. */
  type: z.enum(['waiting_on', 'blocking']),
  taskId: id,
});
export const addLinkSchema = z.object({ taskId: id });

// ---------- Comments and reactions ----------

export const COMMENT_RESOURCE_TYPES = ['task', 'doc', 'clip', 'whiteboard'] as const;

export const commentQuerySchema = z.object({ resourceType: z.enum(COMMENT_RESOURCE_TYPES), resourceId: id });
export const createCommentSchema = z.object({
  resourceType: z.enum(COMMENT_RESOURCE_TYPES),
  resourceId: id,
  parentId: id.nullable().optional(),
  body: richText,
  /** Assign the comment to someone as an action item. */
  assignedTo: id.nullable().optional(),
  meta: z.record(z.string(), z.unknown()).optional(),
  /** Uploaded attachment ids to move onto the comment. */
  attachmentIds: idList.optional(),
});
export const updateCommentSchema = z.object({
  body: richText.optional(),
  resolved: z.boolean().optional(),
  assignedTo: id.nullable().optional(),
  /** Replaces the anchoring data (a pinned note moved to another spot). */
  meta: z.record(z.string(), z.unknown()).optional(),
});
export type CreateCommentInput = z.infer<typeof createCommentSchema>;
export type UpdateCommentInput = z.infer<typeof updateCommentSchema>;

export const toggleReactionSchema = z.object({
  targetType: z.enum(['comment', 'message', 'clip']),
  targetId: id,
  emoji: z.string().min(1).max(16),
});

// ---------- Uploads ----------

export const createUploadSchema = z.object({
  resourceType: z.string().min(1).max(32),
  resourceId: id,
  filename: z.string().min(1).max(255),
  mime: z.string().max(127).default('application/octet-stream'),
  size: z.number().int().min(0),
});
export const completeUploadSchema = z.object({
  parts: z.array(z.object({ partNumber: z.number().int().min(1), etag: z.string() })).max(10_000),
});
/** Parts are at least 5 MiB (R2 minimum); the last part may be smaller. */
export const UPLOAD_PART_BYTES = 8 * 1024 * 1024;

// ---------- Views ----------

const filterRule = z.object({ field: z.string().max(80), op: z.enum(FILTER_OPS), value: z.unknown().optional() });
export const filterGroupSchema: z.ZodType<FilterGroup> = z.lazy(() =>
  z.object({ op: z.enum(['and', 'or']), rules: z.array(z.union([filterRule, filterGroupSchema])).max(50) }),
);

export const viewConfigSchema = z.looseObject({
  filter: filterGroupSchema.optional(),
  sort: z.array(z.object({ field: z.string().max(80), dir: z.enum(['asc', 'desc']) })).max(5).optional(),
  groupBy: z.string().max(80).nullable().optional(),
  columns: z.array(z.string().max(80)).max(60).optional(),
  columnWidths: z.record(z.string(), z.number()).optional(),
  showClosed: z.boolean().optional(),
  meMode: z.boolean().optional(),
  subtasks: z.enum(['nested', 'flat', 'hidden']).optional(),
  collapsedGroups: z.array(z.string()).max(200).optional(),
  cardFields: z.array(z.string().max(80)).max(30).optional(),
  cardCover: z.boolean().optional(),
  columnColors: z.record(z.string().max(80), z.string().regex(/^#[0-9a-fA-F]{6}$/)).optional(),
});

const viewScope = z.enum(['workspace', 'space', 'folder', 'list', 'everything', 'me']);
export const viewQuerySchema = z.object({ scopeType: viewScope, scopeId: id });
export const createViewSchema = z.object({
  scopeType: viewScope,
  scopeId: id,
  type: z.enum(VIEW_TYPES),
  name: nameSchema,
  config: viewConfigSchema.optional(),
  isPrivate: z.boolean().optional(),
});
export const updateViewSchema = z.object({
  name: nameSchema.optional(),
  config: viewConfigSchema.optional(),
  isPrivate: z.boolean().optional(),
  position: positionSchema.optional(),
});
export type CreateViewInput = z.infer<typeof createViewSchema>;
export type UpdateViewInput = z.infer<typeof updateViewSchema>;

export const searchQuerySchema = z.object({ q: z.string().trim().min(1).max(200), limit: z.coerce.number().int().min(1).max(50).optional() });
